import { message as messageAntd } from 'antd'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import axios from 'axios'

import {
  canRetryUnauthorized,
  markUnauthorizedRetry,
  type RetryableRequestConfig,
} from '@/network/axios/authRetryPolicy'
import { axiosBaseOptions } from '@/network/axios/axios-setup'
import type { AxiosDownload, Upload, Uploadxzz, UrlDownload } from '@/network/axios/type'
import { UploadStream } from '@/network/axios/type'
import { isCustomerScopedRequest } from '@/network/customerScope'
import { clearTokens, getAccessToken, refreshAccessToken } from '@/network/tokenManager'
import { type CustomerOption, requireCustomerSelection, useCustomerContextStore } from '@/store/customerContext'
import { useUserStore } from '@/store/user'

//优先采用RFC 5897  让与url直接通过a标签的下载的结果相同
function analysisFilename(contentDisposition: string): string {
  let regex = /filename\*=\S+?''(.+?)(;|$)/
  if (regex.test(contentDisposition)) {
    return RegExp.$1
  }
  regex = /filename="{0,1}([\S\s]+?)"{0,1}(;|$)/
  if (regex.test(contentDisposition)) {
    return RegExp.$1
  }
  return '文件名获取异常'
}

function objToFormData(obj: Uploadxzz) {
  const formData = new FormData()
  Object.keys(obj).forEach((key) => {
    formData.append(key, obj[key])
  })
  return formData
}

class MyAxios {
  private readonly axiosInstance: AxiosInstance
  private isRedirecting = false
  private customerContextLoading: Promise<CustomerOption[]> | null = null

  constructor(options: AxiosRequestConfig) {
    this.axiosInstance = axios.create(options)
    this.initInterceptors()
  }

  private initInterceptors() {
    // ***请求拦截器***************************************************
    // 请求拦截  上传数据的加密处理在这里配置
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // 如果正在跳转，取消所有请求
        if (this.isRedirecting) {
          return Promise.reject(new Error('正在跳转登录页面，取消请求'))
        }

        //headers的access-token部分在请求拦截中加入
        const token = getAccessToken()
        if (token) {
          config.headers['authorization'] = `Bearer ${token}`
          // config.headers["token"] = `${token}`
        }
        if (token && isCustomerScopedRequest(String(config.url || ''), config.method)) {
          const userId = useUserStore.getState().userInfo.id
          const context = useCustomerContextStore.getState()
          let customerId = context.ownerUserId === userId ? context.currentCustomerId : null
          if (!customerId) {
            await this.loadCustomerContext(userId)
            customerId = useCustomerContextStore.getState().currentCustomerId
          }
          if (!customerId) {
            requireCustomerSelection()
            return Promise.reject(new Error('请先选择客户'))
          }
          config.headers['X-Customer-Id'] = String(customerId)
        }
        return config
      },
      (error) => {
        // 工单 #317：仅打印错误消息，error 对象可能携带含凭据的请求体
        console.warn('axios请求拦截部分报错:', error?.message)
        error.code == 'ERR_NETWORK' && messageAntd.error('设备离线,请检查网络')
        return Promise.reject(error)
      },
    )
    // ***响应拦截器*****************************************************
    //响应拦截  从接口响应的数据在这里处理 例如解密等  时间发生在then catch前
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // platform 成功: code=0；旧 model_api / mall: code=200
        const code = response.data?.code
        if (code === 0) {
          return response.data
        }

        console.error('code异常---axios', response)
        const msg = response.data.message || response.data.msg || '请求失败'
        messageAntd.error(`${response.data.code}!${msg}`)
        return Promise.reject(Object.assign(new Error(String(msg)), { response: { data: response.data } }))
      },
      async (error) => {
        const status = error?.response?.status
        const errorCode = String(error?.response?.data?.code || error?.response?.data?.errorCode || '')
        const serverMessage = error?.response?.data?.message || error?.response?.data?.msg || error?.message

        if (errorCode === '710002') {
          requireCustomerSelection()
          messageAntd.error('当前客户已失效，请重新选择')
          return Promise.reject(error)
        }

        // 401：尝试刷新令牌，失败再跳登录
        if (status === 401) {
          const originalConfig = error?.config as RetryableRequestConfig | undefined
          const isLoginPage = /^\/login(?:\/|$)/.test(window.location.pathname)
          const isAuthRequest = /\/auth\/(login|register|refresh|login-by-sms)/.test(String(originalConfig?.url || ''))

          // 登录/注册/刷新接口本身的 401：只提示原因，不踢登录跳转
          if (isLoginPage || isAuthRequest) {
            messageAntd.error(serverMessage || '账号或密码错误')
            return Promise.reject(error)
          }

          // 同一业务请求只允许刷新并重放一次；重放仍为 401 时立即结束登录态。
          if (!canRetryUnauthorized(originalConfig)) {
            await this.redirectToLogin()
            return Promise.reject(error)
          }

          // 在等待共享刷新队列前先打标，避免重放请求再次进入刷新流程。
          markUnauthorizedRetry(originalConfig)

          try {
            const newToken = await refreshAccessToken()
            originalConfig.headers = originalConfig.headers || {}
            originalConfig.headers['authorization'] = `Bearer ${newToken}`
            return this.axiosInstance(originalConfig)
          } catch {
            await this.redirectToLogin()
            return Promise.reject(error)
          }
        }
        //429
        else if (status === 429) {
          messageAntd.open({
            key: '429toomanyrequest',
            type: 'warning',
            content: '429!请求失败, 原因: ' + serverMessage,
          })
        }
        //400
        else if (status === 400) {
          messageAntd.error(serverMessage || '请求参数错误')
          return Promise.reject(error)
        }
        //404：接口不存在，不再误报「网络异常」
        else if (status === 404) {
          messageAntd.error(serverMessage || '接口不存在')
          return Promise.reject(error)
        }
        // 其他
        else {
          console.error('error异常---axios', error)
          messageAntd.open({
            key: 'unknownerror',
            type: 'warning',
            content: error?.response
              ? `请求失败(${status}): ${serverMessage || '请稍后重试'}`
              : '网络异常, 检查网络或后端服务是否正常!',
          })
        }
        return Promise.reject(error)
      },
    )
  }

  async loadCustomerContext(ownerUserId: number): Promise<CustomerOption[]> {
    if (!this.customerContextLoading) {
      this.customerContextLoading = (
        this.axiosInstance.get('customer-context/customers') as unknown as Promise<{
          data: CustomerOption[]
        }>
      )
        .then((response) => {
          const customers = response.data ?? []
          useCustomerContextStore.getState().setCustomers(ownerUserId, customers)
          return customers
        })
        .finally(() => {
          this.customerContextLoading = null
        })
    }
    return this.customerContextLoading
  }

  private async redirectToLogin(): Promise<void> {
    if (this.isRedirecting) return

    this.isRedirecting = true
    clearTokens()
    useUserStore.getState().resetUser()
    messageAntd.open({
      key: '401',
      type: 'error',
      content: '登录过期,即将跳转重新登录!',
    })
    await new Promise((resolve) => setTimeout(resolve, 2000))
    const redirect = encodeURIComponent(`${window.location.pathname}${window.location.search}`)
    window.location.replace(`/login?redirect=${redirect}`)
  }

  get<T>(url: string, params?: object): Promise<T> {
    return this.axiosInstance.get(url, { params })
  }

  post<T>(url: string, data?: object, params?: object): Promise<T> {
    return this.axiosInstance.post(url, data, params)
  }

  put<T>(url: string, data?: object, params?: object): Promise<T> {
    return this.axiosInstance.put(url, data, { params })
  }

  delete<T>(url: string, config?: object): Promise<T> {
    return this.axiosInstance.delete(url, config)
  }

  patch<T>(url: string, data?: object): Promise<T> {
    return this.axiosInstance.patch(url, data)
  }

  upload<T>(data: Upload): Promise<T> {
    const { url, formData, controller, onUploadProgress } = data
    return this.axiosInstance.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      signal: controller ? controller.signal : undefined, //用于文件上传可以取消  只需在外部调用controller.abort()即可。 参考//https://juejin.cn/post/6954919023205154824
    })
  }

  uploadxzz<T>(url: string, data: Uploadxzz): Promise<T> {
    const { file, ...rest } = data
    return this.axiosInstance.post(url, objToFormData({ ...rest, file }), {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  async uploadStream<T>(data: UploadStream): Promise<T> {
    const { url, file, controller, onUploadProgress } = data
    /** generateSHA 生成文件SHA256 hash  用于标识文件唯一性 往往会用上 这里会用到crypto-js库 **/
    // async function generateSHA(file: File): Promise<string> {
    //   const wordArray = CryptoJs.lib.WordArray.create(await file.arrayBuffer())
    //   const sha256 = CryptoJs.SHA256(wordArray)

    //   return sha256.toString()
    // }
    // const Hash = await generateSHA(File)
    const fileArrayBuffer = await file.arrayBuffer()
    return this.axiosInstance.post(url, fileArrayBuffer, {
      headers: { 'Content-Type': 'application/octet-stream' },
      onUploadProgress,
      signal: controller ? controller.signal : undefined, //用于文件上传可以取消  只需在外部调用controller.abort()即可。 参考//https://juejin.cn/post/6954919023205154824
    })
  }

  axiosDownload(params: AxiosDownload): Promise<{ fileName: string }> {
    const { url, data, controller, fileName, onDownloadProgress } = params
    return new Promise((resolve, reject) => {
      this.axiosInstance
        .get<Blob>(url, {
          params: data,
          responseType: 'blob',
          onDownloadProgress,
          signal: controller ? controller.signal : undefined, //用于文件下载可以取消  只需在外部调用controller.abort()即可。 参考//https://juejin.cn/post/6954919023205154824以及https://axios-http.com/zh/docs/cancellation
        })
        .then((res) => {
          const blob = new Blob([res.data])
          const a = document.createElement('a')
          a.style.display = 'none'
          if (fileName) {
            a.download = fileName
          } else {
            a.download = decodeURIComponent(analysisFilename(res.headers['content-disposition']))
          }
          a.href = URL.createObjectURL(blob)
          document.body.appendChild(a)
          const downloadFileName = a.download
          a.click()
          URL.revokeObjectURL(a.href)
          document.body.removeChild(a)
          resolve({ fileName: downloadFileName })
        })
        .catch((err) => {
          reject(err)
        })
    })
  }

  urlDownload(params: UrlDownload) {
    const { fileName, serveBaseUrl = import.meta.env.VITE_API_SERVER_URL, fileUrl } = params
    const a = document.createElement('a')
    a.style.display = 'none'
    a.download = fileName
    a.href = fileUrl.startsWith('http') ? fileUrl : `${serveBaseUrl}${fileUrl}`
    document.body.appendChild(a)
    a.click()
    URL.revokeObjectURL(a.href) // 释放URL 对象
    document.body.removeChild(a)
  }
}

export const request = new MyAxios(axiosBaseOptions)
